import { useSyncExternalStore } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { landingTiles } from '@/features/home/data/landing-tiles'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BreadcrumbSegment {
  label: string
  path: string
}

// ---------------------------------------------------------------------------
// Static reverse lookup: path -> { moduleSlug, moduleTitle, pageLabel }
// ---------------------------------------------------------------------------

interface PathInfo {
  moduleSlug: string
  moduleTitle: string
  pageLabel: string
}

const pathMap = new Map<string, PathInfo>()

for (const tile of landingTiles) {
  for (const link of tile.links) {
    // Strip query params for the lookup key (e.g. '/armoury?tab=ammunition' -> '/armoury')
    const pathname = link.path.split('?')[0] ?? link.path
    // Only store the first match (some paths appear in multiple modules)
    if (!pathMap.has(pathname)) {
      pathMap.set(pathname, {
        moduleSlug: tile.slug,
        moduleTitle: tile.title,
        pageLabel: link.label,
      })
    }
  }
}

// Slug -> title map for module hub pages
const slugTitleMap = new Map(landingTiles.map((t) => [t.slug, t.title]))

// ---------------------------------------------------------------------------
// Detail-page label store
//
// Detail pages (e.g. CertificateDetailPage, FirearmDetailPage) call
// `setDetailLabel('Firearms Certificate')` so the breadcrumb can display a
// meaningful name instead of a UUID.  The store uses useSyncExternalStore so
// the Breadcrumbs component re-renders when the label changes.
// ---------------------------------------------------------------------------

let _detailLabel = ''
const _listeners = new Set<() => void>()

/** Set the breadcrumb label for the current detail page. Call with '' to clear. */
export function setDetailLabel(label: string) {
  _detailLabel = label
  _listeners.forEach((l) => l())
}

function _subscribe(cb: () => void) {
  _listeners.add(cb)
  return () => {
    _listeners.delete(cb)
  }
}

function _getSnapshot() {
  return _detailLabel
}

function useDetailLabel() {
  return useSyncExternalStore(_subscribe, _getSnapshot)
}

// ---------------------------------------------------------------------------
// Pure function: build breadcrumb segments from a pathname
// ---------------------------------------------------------------------------

/** Convert a slug to a Title Case label (e.g. 'some-deep-path' -> 'Some Deep Path') */
function slugToLabel(slug: string): string {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Placeholder used for the last segment of a detail page breadcrumb.
 * The Breadcrumbs component replaces it with the value from the detail-label
 * store.  If the store is empty the segment is hidden so users never see it.
 */
export const DETAIL_PLACEHOLDER = '\x00'

export function buildBreadcrumbs(pathname: string): BreadcrumbSegment[] {
  const dashboard: BreadcrumbSegment = { label: 'Dashboard', path: '/' }

  // Home page
  if (pathname === '/') {
    return [dashboard]
  }

  // Module hub page: /module/:slug
  const moduleMatch = pathname.match(/^\/module\/(.+)$/)
  if (moduleMatch) {
    const slug = moduleMatch[1] ?? ''
    const title = slugTitleMap.get(slug) ?? slugToLabel(slug)
    return [dashboard, { label: title, path: pathname }]
  }

  // Known module page (e.g. /shoot-log -> My Shooting)
  const info = pathMap.get(pathname)
  if (info) {
    return [
      dashboard,
      { label: info.moduleTitle, path: `/module/${info.moduleSlug}` },
      { label: info.pageLabel, path: pathname },
    ]
  }

  // Detail page: /parent/:id (e.g. /wallet/:id, /armoury/:id)
  // Strip the last segment and look up the parent path in pathMap.
  const parts = pathname.split('/').filter(Boolean)
  if (parts.length >= 2) {
    const parentPath = '/' + parts[0]
    const parentInfo = pathMap.get(parentPath)
    if (parentInfo) {
      return [
        dashboard,
        { label: parentInfo.moduleTitle, path: `/module/${parentInfo.moduleSlug}` },
        { label: parentInfo.pageLabel, path: parentPath },
        { label: DETAIL_PLACEHOLDER, path: pathname },
      ]
    }
  }

  // Orphan page (e.g. /profile)
  const segment = pathname.split('/').filter(Boolean).pop() ?? ''
  return [dashboard, { label: slugToLabel(segment), path: pathname }]
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Breadcrumbs() {
  const { pathname } = useLocation()
  const detailLabel = useDetailLabel()
  const raw = buildBreadcrumbs(pathname)

  // Replace placeholder with the detail-label value; hide if not yet set
  const segments = raw
    .map((seg) => (seg.label === DETAIL_PLACEHOLDER ? { ...seg, label: detailLabel } : seg))
    .filter((seg) => seg.label !== '')

  return (
    <nav aria-label="Breadcrumb" className="mb-3 flex items-center gap-1 text-sm">
      {segments.map((segment, index) => {
        const isLast = index === segments.length - 1

        return (
          <span key={segment.path} className="flex items-center gap-1">
            {index > 0 && <ChevronRight className="h-3.5 w-3.5 text-gray-300 dark:text-gray-600" />}
            {isLast ? (
              <span className="text-gray-500 dark:text-gray-400">{segment.label}</span>
            ) : (
              <Link
                to={segment.path}
                className="text-gray-400 transition-colors hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-300"
              >
                {segment.label}
              </Link>
            )}
          </span>
        )
      })}
    </nav>
  )
}
